import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'
import { CollapsibleSection } from './CollapsibleSection.tsx'

const MAX_GUEST_NAME = 200

// Zero's MutatorResult is not re-exported from the package entry point, so the
// pair of optimistic/authoritative promises is described structurally here.
type MutationOutcome =
  | { readonly type: 'success' }
  | { readonly type: 'error'; readonly error: { readonly message: string } }
interface MutationHandle {
  client: Promise<MutationOutcome>
  server: Promise<MutationOutcome>
}

/**
 * US-13 guest management, shown to the owning member and to admins. Guests are
 * soft-deleted, so their historical drinks stay on the owner's tab; the
 * financial assignment never moves off the owning member.
 */
export function GuestList({ userId }: { userId: string }) {
  const z = useZero()
  const [guests] = useQuery(queries.guestsForUser({ userId }))
  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  /** Await the optimistic write, then reconcile against the authoritative one. */
  async function run(
    mutation: MutationHandle,
    pending: string,
    done: string,
  ): Promise<boolean> {
    const clientResult = await mutation.client
    if (clientResult.type === 'error') {
      setMessage(clientResult.error.message)
      return false
    }
    setMessage(pending)
    void mutation.server.then((serverResult) => {
      setMessage(serverResult.type === 'success' ? done : serverResult.error.message)
    })
    return true
  }

  async function addGuest(event: React.FormEvent) {
    event.preventDefault()
    const name = newName.trim()
    if (!name) {
      setMessage('Enter a guest name')
      return
    }
    setBusy(true)
    setMessage('')
    try {
      const ok = await run(
        z.mutate(mutators.guests.add({ id: crypto.randomUUID(), userId, name })),
        'Guest added · syncing…',
        'Guest added',
      )
      if (ok) setNewName('')
    } catch {
      setMessage('Failed to add guest')
    } finally {
      setBusy(false)
    }
  }

  async function saveRename(id: string) {
    const name = editingName.trim()
    if (!name) {
      setMessage('A guest name cannot be empty')
      return
    }
    setMessage('')
    try {
      const ok = await run(
        z.mutate(mutators.guests.rename({ id, name })),
        'Guest renamed · syncing…',
        'Guest renamed',
      )
      if (ok) setEditingId(null)
    } catch {
      setMessage('Failed to rename guest')
    }
  }

  async function removeGuest(id: string, name: string) {
    if (!window.confirm(`Remove ${name}? Their past drinks stay on this tab.`)) return
    setMessage('')
    try {
      await run(
        z.mutate(mutators.guests.remove({ id })),
        'Guest removed · syncing…',
        'Guest removed',
      )
    } catch {
      setMessage('Failed to remove guest')
    }
  }

  return (
    <CollapsibleSection title="Guests" count={guests.length}>
      <div className="card">
        {guests.length === 0 ? (
          <p className="empty">No guests yet.</p>
        ) : (
          guests.map((guest) =>
            editingId === guest.id ? (
              <div className="line" key={guest.id}>
                <input
                  className="inline-edit"
                  autoFocus
                  maxLength={MAX_GUEST_NAME}
                  value={editingName}
                  onChange={(event) => setEditingName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void saveRename(guest.id)
                    if (event.key === 'Escape') setEditingId(null)
                  }}
                />
                <button className="btn-ghost" onClick={() => void saveRename(guest.id)}>
                  Save
                </button>
                <button className="btn-ghost" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="line" key={guest.id}>
                <div className="line-main">
                  <Link
                    className="text-link"
                    to="/users/$userId/guests/$guestId"
                    params={{ userId, guestId: guest.id }}
                  >
                    {guest.name}
                  </Link>
                </div>
                <button
                  className="btn-ghost"
                  onClick={() => {
                    setEditingId(guest.id)
                    setEditingName(guest.name)
                  }}
                >
                  Rename
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => void removeGuest(guest.id, guest.name)}
                  title="Remove guest"
                >
                  ✕
                </button>
              </div>
            ),
          )
        )}

        <form className="guest-add" onSubmit={addGuest}>
          <input
            placeholder="Add a guest"
            maxLength={MAX_GUEST_NAME}
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <button className="btn btn-sm" type="submit" disabled={busy || !newName.trim()}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </form>
        {message ? <p className="form-message">{message}</p> : null}
      </div>
    </CollapsibleSection>
  )
}
